import { useState } from "react";
import { notification } from "antd";
import dayjs from "dayjs";
import { createSupportTicket } from "services/supportTickets.service";

/**
 * React Router and some libs throw plain objects, not Error instances.
 * Converts anything into an Error with a useful message + stack when available.
 */
export function normalizeReportError(raw) {
  if (raw === null || raw === undefined) return null;
  if (raw instanceof Error) return raw;
  if (typeof raw === "string") return new Error(raw);

  if (typeof raw !== "object") return new Error(String(raw));

  // RouteErrorResponse / thrown Response shape (React Router)
  const status = raw.status;
  const statusText = raw.statusText;
  const data = raw.data;
  if (
    (status !== null && status !== undefined) ||
    (statusText !== null && statusText !== undefined)
  ) {
    let detail = "";
    if (data !== null && data !== undefined) {
      if (typeof data === "string") detail = data;
      else if (typeof data === "object") {
        try {
          detail = JSON.stringify(data);
        } catch {
          detail = String(data);
        }
      } else detail = String(data);
      if (detail.length > 400) detail = `${detail.slice(0, 400)}…`;
    }
    const msg = [
      statusText ||
        (status !== null && status !== undefined ? `HTTP ${status}` : "Route error"),
      detail && detail !== "{}" ? detail : "",
    ]
      .filter(Boolean)
      .join(": ");
    const e = new Error(msg || "Route error");
    e.name = "RouteError";
    if (raw.stack) e.stack = String(raw.stack);
    return e;
  }

  // Plain thrown object with message (often { message, stack, name })
  if (
    raw.message !== null &&
    raw.message !== undefined &&
    String(raw.message).trim() !== ""
  ) {
    const e = new Error(String(raw.message));
    e.name = raw.name ? String(raw.name) : "Error";
    if (raw.stack) e.stack = String(raw.stack);
    return e;
  }

  try {
    const s = JSON.stringify(raw);
    return new Error(`Thrown value: ${s.slice(0, 500)}${s.length > 500 ? "…" : ""}`);
  } catch {
    return new Error("Thrown non-Error value (could not serialize)");
  }
}

/**
 * Builds the full structured error report body.
 * @param {{ error?: unknown, componentStack?: string, context?: string }} opts
 */
export function buildErrorReportBody({ error, componentStack, context } = {}) {
  const err = normalizeReportError(error);
  const msgTrim = err ? String(err.message ?? "").trim() : "";
  const hasMsg = msgTrim !== "";
  const isGenericMessage = /^unknown error$/i.test(msgTrim);

  let thrownSnapshot = null;
  if (
    isGenericMessage &&
    error !== null &&
    error !== undefined &&
    typeof error === "object" &&
    !(error instanceof Error)
  ) {
    try {
      thrownSnapshot = `Thrown payload: ${JSON.stringify(error).slice(0, 600)}`;
    } catch {
      thrownSnapshot = "Thrown payload: (could not serialize)";
    }
  }

  // Keep only the first 6 stack frames — enough to pinpoint the crash
  const shortStack = (err?.stack ?? "")
    .split("\n")
    .slice(0, 6)
    .join("\n");

  // Keep only the first 5 component tree lines
  const shortTree = (componentStack ?? "")
    .trim()
    .split("\n")
    .slice(0, 5)
    .join("\n");

  const timeOnPage = performance.now();
  const minutes = Math.floor(timeOnPage / 60000);
  const seconds = Math.floor((timeOnPage % 60000) / 1000);

  const parts = [
    hasMsg && !isGenericMessage
      ? `[${err.name ?? "Error"}] ${err.message}`
      : `Reported from: ${context ?? "unknown page"}`,
    context && hasMsg && !isGenericMessage ? `Context: ${context}` : null,
    thrownSnapshot,
    `Route: ${window.location.pathname}${window.location.search || ""}`,
    `Crashed at: ${dayjs().format("MMM D, YYYY h:mm:ss A")} (${minutes}m ${seconds}s after page load)`,
    shortStack ? `\nStack:\n${shortStack}` : null,
    shortTree ? `\nComponent tree:\n${shortTree}` : null,
  ]
    .filter((p) => p !== null)
    .join("\n");

  // Hard truncate to stay within the 1000 char API limit
  return parts.length > 1000 ? parts.slice(0, 997) + "..." : parts;
}

/**
 * Hook that manages the state and logic for reporting an error as a support ticket.
 * Returns { reporting, reported, report } — call report({ error, componentStack, context }) on button click.
 */
function useReportError() {
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  const report = async ({ error, componentStack, context } = {}) => {
    if (reported) return;
    setReporting(true);
    const { errors } = await createSupportTicket({
      data: {
        subject: `Technical Error — ${dayjs().format("MMM D, YYYY h:mm A")}`,
        body: buildErrorReportBody({ error, componentStack, context }),
      },
    });
    setReporting(false);
    if (errors?.length) {
      notification.error({
        message: "Failed to submit report. Please try again.",
      });
      return;
    }
    setReported(true);
    notification.success({
      message: "Error reported",
      description: "Our team has been notified. Thank you!",
    });
  };

  return { reporting, reported, report };
}

export default useReportError;
